import { io, Socket } from 'socket.io-client';
import { WPFMessageType } from '@/enums';
import { useConfigStore } from '@/stores/configStore';

interface Command {
    userId: string;
    kioskCode: string;
    commandType: string;
}

class KioskClient {
    private serverUrl: string;
    private kioskCode: string;
    private socket: Socket | null = null;
    private heartbeatInterval: NodeJS.Timeout | null = null;
    private isConnected = false;

    constructor(serverUrl: string, kioskCode: string) {
        this.serverUrl = serverUrl;
        this.kioskCode = kioskCode;
    }

    async connect(): Promise<void> {
        try {
            this.socket = io(`${this.serverUrl}/websocket`, {
                transports: ['polling', 'websocket'],
                timeout: 10000,
            });

            this.setupEventHandlers();
            await this.registerClient();
            this.startHeartbeat();
            this.isConnected = true;
        } catch {
            throw new Error('Failed to connect');
        }
    }

    private setupEventHandlers(): void {
        if (!this.socket) return;

        this.socket.on('kiosk-command-received', (command: Command) => {
            this.handleCommand(command);
        });
    }

    private async registerClient(): Promise<void> {
        if (!this.socket) {
            throw new Error('Socket not connected');
        }
        this.socket.emit('register-client', {
            type: 'kiosk',
            kioskCode: this.kioskCode,
        });
    }

    private startHeartbeat(): void {
        this.heartbeatInterval = setInterval(() => {
            if (this.isConnected) {
                this.sendHeartbeat();
            }
        }, 180000);

        this.sendHeartbeat();
    }

    private stopHeartbeat(): void {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    private sendHeartbeat(): void {
        if (!this.socket) return;

        const heartbeatData = {
            type: 'kiosk',
            kioskCode: this.kioskCode,
        };

        this.socket.emit('heartbeat', heartbeatData);
    }

    private async handleCommand(command: Command): Promise<void> {
        if (!this.socket) {
            throw new Error('Socket not connected');
        }

        try {
            await this.executeCommand(command);
            this.socket.emit('kiosk-command-result', {
                userId: command.userId,
                kioskCode: this.kioskCode,
                commandType: command.commandType,
                success: true,
            });
        } catch (error) {
            console.error(`Command ${command.commandType} failed:`, error);
        }
    }

    private async executeCommand(command: Command): Promise<any> {
        switch (command.commandType) {
            case 'shutdown':
                return await this.simulateShutdown();

            case 'restart':
                return await this.simulateRestart();

            case 're-fetch-config': {
                const { fetchConfig } = useConfigStore.getState();
                await sessionStorage.removeItem('lastConfigFetch');
                await fetchConfig();
                return { success: true, command: 're-fetch-config' };
            }

            default:
                throw new Error(`Unknown command type: ${command.commandType}`);
        }
    }

    private async simulateShutdown(): Promise<any> {
        if (window.chrome && window.chrome.webview) {
            const message = {
                type: WPFMessageType.Shutdown,
                timestamp: new Date().toISOString(),
            };
            window.chrome.webview.postMessage(message);
        }

        return { success: true, command: 'shutdown' };
    }

    private async simulateRestart(): Promise<any> {
        if (window.chrome && window.chrome.webview) {
            const message = {
                type: WPFMessageType.Restart,
                timestamp: new Date().toISOString(),
            };
            window.chrome.webview.postMessage(message);
        }

        return { success: true, command: 'restart' };
    }

    public disconnect(): void {
        this.isConnected = false;
        this.stopHeartbeat();
        if (this.socket) {
            this.socket.disconnect();
        }
    }
}

let kioskClientInstance: KioskClient | null = null;

export const createKioskClient = (serverUrl: string, kioskCode: string): KioskClient => {
    if (kioskClientInstance) {
        kioskClientInstance.disconnect();
    }
    kioskClientInstance = new KioskClient(serverUrl, kioskCode);
    return kioskClientInstance;
};

export const getKioskClient = (): KioskClient | null => {
    return kioskClientInstance;
};

export default KioskClient;
